import { type ReactNode } from 'react'

interface FrankCardProps {
  // Filename in /public — e.g. "frank-shrug.png"
  src: string
  alt: string
  // The mono kicker label — like "● FRANK · OFFLINE" or "/ memory full"
  badge?: string
  // Optional badge color tint
  badgeKind?: 'neon' | 'warning' | 'error'
  // Card width preset
  size?: 'sm' | 'md' | 'lg'
  // Optional tinted backdrop colour (overrides default neon glow)
  glow?: 'neon' | 'warning' | 'error'
  // Optional caption shown below the image
  caption?: ReactNode
  // Optional className passthrough for outer wrapper
  className?: string
}

// Wraps any of the cropped Frank PNGs in a framed card with a backdrop
// glow + brand-mono badge. The PNGs ship with off-white/gray backgrounds
// from the source sprite sheet, so the card frames hide that and let
// each pose feel intentional on our dark theme without needing a
// background-removal pass.
//
// Drop one of these anywhere a state needs Frank to show his face:
//   <FrankCard src="frank-shrug.png" alt="..." badge="/ no datasets yet" />
//   <FrankCard src="frank-server-offline.png" badge="● server · offline" badgeKind="error" />
export default function FrankCard({
  src,
  alt,
  badge,
  badgeKind = 'neon',
  size = 'md',
  glow = 'neon',
  caption,
  className = '',
}: FrankCardProps) {
  const sizeCls =
    size === 'sm' ? 'w-32 md:w-40' : size === 'lg' ? 'w-72 md:w-96' : 'w-48 md:w-56'

  const glowColor =
    glow === 'warning'
      ? 'rgba(255,170,0,0.18)'
      : glow === 'error'
      ? 'rgba(255,68,68,0.18)'
      : 'rgba(57,255,20,0.16)'

  const badgeCls =
    badgeKind === 'warning'
      ? 'text-warning border-warning/40 bg-warning/5'
      : badgeKind === 'error'
      ? 'text-error border-error/40 bg-error/5'
      : 'text-neon border-neon/40 bg-neon/5'

  return (
    <div className={`relative inline-flex flex-col items-center ${className}`}>
      {/* Soft tinted glow behind the card — gives Frank a halo, also
          masks the off-white sprite background so it reads as intentional */}
      <div
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[140%] h-[140%] rounded-full pointer-events-none"
        style={{
          background: `radial-gradient(circle, ${glowColor} 0%, transparent 65%)`,
          filter: 'blur(28px)',
        }}
      />
      <div className="relative z-10 rounded-2xl border border-border bg-bg-card overflow-hidden">
        <img
          src={`/${src}`}
          alt={alt}
          className={`${sizeCls} h-auto select-none`}
          draggable={false}
        />
      </div>
      {badge && (
        <span
          className={`relative z-10 mt-3 font-mono text-[9px] uppercase tracking-[0.25em] px-2 py-1 rounded-md border ${badgeCls}`}
        >
          {badge}
        </span>
      )}
      {caption && (
        <div className="relative z-10 mt-3 max-w-md text-center text-xs text-text-secondary italic leading-relaxed">
          {caption}
        </div>
      )}
    </div>
  )
}
